import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCalendar, FiClock, FiActivity, FiLayers, FiCheckCircle } = FiIcons;

const FeatureCard = ({ title, description, image, icon: Icon, index }) => (
  <motion.article 
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.6, delay: index * 0.1 }}
    className="group relative"
  >
    <div className="relative z-10 bg-white rounded-[32px] p-2 border border-line/40 shadow-soft transition-all duration-500 group-hover:shadow-card group-hover:-translate-y-2">
      <div className="relative aspect-[4/5] rounded-[24px] overflow-hidden bg-paper">
        <img 
          src={image} 
          alt={title} 
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
          loading="lazy" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-aubergine/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
      
      <div className="p-6">
        <div className="w-12 h-12 rounded-2xl bg-rose/10 flex items-center justify-center text-rose mb-5 group-hover:bg-rose group-hover:text-white transition-colors duration-300">
          <SafeIcon icon={Icon} className="w-6 h-6" />
        </div>
        <h3 className="text-xl font-bold text-aubergine mb-3">{title}</h3>
        <p className="text-muted text-[0.95rem] leading-relaxed">{description}</p>
      </div>
    </div>
  </motion.article>
);

const FeaturesSection = () => {
  const features = [
    {
      title: "Health Timeline",
      description: "Securely log periods, symptoms, and moods. Understand your body's patterns with intuitive data visualization.",
      image: "https://media-manager-c.questera.ai/greta-media/cb626cea481d7a3becbea24c974d0b65e32a5ba3c552e12f2d9768775a77545899023c8e9d705cdece5224b19de37c3e/images/aW1hZ2UvcG5n/d7d7f1c063beaf8957071f85d4a34c8b.png",
      icon: FiCalendar
    },
    {
      title: "Spiritual Status",
      description: "Accurate prayer times and qibla. Automatically understand your worship status based on your cycle logs.",
      image: "https://media-manager-c.questera.ai/greta-media/cb626cea481d7a3becbea24c974d0b65e32a5ba3c552e12f2d9768775a77545899023c8e9d705cdece5224b19de37c3e/images/aW1hZ2UvcG5n/470077c706d0a9d499aeab8c6d61ee29.png",
      icon: FiClock
    },
    {
      title: "Fasting Assistant",
      description: "Manage missed fasts, record voluntary worship, and track your progress through Ramadan and beyond.",
      image: "https://media-manager-c.questera.ai/greta-media/cb626cea481d7a3becbea24c974d0b65e32a5ba3c552e12f2d9768775a77545899023c8e9d705cdece5224b19de37c3e/images/aW1hZ2UvcG5n/90f0babce36d354e8afe78add75ac3bf.png",
      icon: FiActivity
    }
  ];

  return (
    <section id="features" className="py-24 lg:py-32 bg-white">
      <div className="w-full max-w-[1120px] mx-auto px-4">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6">
          <div className="max-w-[640px]">
            <h2 className="text-[clamp(2.4rem,4vw,3.5rem)] mb-6">Built for your daily life, designed for your privacy.</h2>
            <p className="text-lg text-muted">
              Nisaa combines essential tracking tools with a beautiful, ad-free interface that respects your data and your faith.
            </p>
          </div>
          <div className="flex gap-4">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-aubergine">0%</span>
              <span className="text-xs text-muted uppercase font-bold tracking-widest">Ads</span>
            </div>
            <div className="w-px h-12 bg-line" />
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-aubergine">100%</span>
              <span className="text-xs text-muted uppercase font-bold tracking-widest">Private</span>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {features.map((feature, idx) => (
            <FeatureCard key={idx} index={idx} {...feature} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;